/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { ApolloLink } from '@apollo/client';
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
  SSRMultipartLink,
} from '@apollo/client-integration-nextjs';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';

import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { useCallback } from 'react';
import { useCookies } from 'react-cookie';
import { setVerbosity } from 'ts-invariant';

import { getCountry, getIp } from '../utils/headers';
import { useHeadersContext } from './';
import { typePolicies } from './apollo/typePolicies';
import { useRawInitData } from '@tma.js/sdk-react';
import { AnimatedLogoFallback } from '../components/AnimatedLogoFallback';

if (process.env.NODE_ENV === 'development') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
  setVerbosity('error');
}

function makeClient(
  headers: Record<string, string>,
  cookies: Record<string, string>,
  authData: string,
) {
  const {
    'user-agent': userAgent,
    host,
    ['x-forwarded-proto']: protocol,
  } = headers;
  const isLocal =
    protocol === 'http' ||
    host.includes('localhost') ||
    process.env.NODE_ENV === 'development';
  const isDocker = headers['x-docker'];
  const urlApi = isDocker
    ? 'localhost:3000'
    : isLocal
      ? (process.env.NEXT_PUBLIC_API_DOMAIN ?? 'api.shotix.fun')
      : host;
  const ip = getIp(headers);
  const country = getCountry(headers);
  const botName = cookies['x-bot-name'] ?? headers['x-bot-name'];

  console.log(botName);

  const defaultHeaders = {
    'x-auth-ip': ip,
    'x-country': country,
    'x-user-agent': userAgent,
    'x-segment-ab': cookies.segment_ab,
    'x-ga-ab': cookies.ga_ab,
    'x-ga-abc': cookies.ga_abc,
    'x-auth-telegram': authData,
    'x-bot-name': botName,
  };

  const uploadLink = new UploadHttpLink({
    uri: `${isDocker ? 'http' : 'https'}://${urlApi}/graphql`,
    headers: defaultHeaders,
  }) as unknown as ApolloLink;

  return new ApolloClient({
    // connectToDevTools: true,
    cache: new InMemoryCache({
      typePolicies,
    }),
    link:
      typeof window === 'undefined'
        ? ApolloLink.from([
            new SSRMultipartLink({
              stripDefer: true,
            }),
            uploadLink,
          ])
        : uploadLink,
  });
}

export function ApolloProvider({ children }: { children: React.ReactNode }) {
  if (typeof window === 'undefined') {
    return <AnimatedLogoFallback />;
  }
  const rawInitData = useRawInitData();

  const headers = useHeadersContext();
  const [cookies] = useCookies(['segment_ab', 'ga_ab', 'ga_abc']);

  const createClientCallback = useCallback(
    () => makeClient(headers, cookies, rawInitData!),
    [],
  );

  return (
    <ApolloNextAppProvider makeClient={createClientCallback}>
      {children}
    </ApolloNextAppProvider>
  );
}
